import React, { useState, useEffect } from 'react'

import { format } from 'date-fns'
import styled from 'styled-components'

import { Paragraph } from '@atoms/index'
import { ResponsivePXValue, Heading, Spacer } from '@client/components'
import { Form, RadioButton, RadioButtonProps, SectionLoading, useForm } from '@client/components/molecules'
import { DeliveryDateFragment, useUserDetailsQuery, useUserCartQuery, useUpdateUserSubscription } from '@hooks/api'
import { FieldData } from '@organisms/modals'
import { CityDeliveryDaysEnum, WeekDayEnum } from '@uctypes/api/globalTypes'

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .delivery-day-form {
    width: 100%;

    .radio-group-container, .radio-item-container {
      justify-content: flex-start;
      margin-left: 0;
      margin-right: 0;
      ${ResponsivePXValue('width', { mobile: '90%', tablet: '90%', desktop: '100%' })};
    }
  }
`

export interface CheckoutDeliveryDaySelectProps {
  title?: string
  subTitle?: string
  onDeliverDaySelect?: (deliveryDay?: CityDeliveryDaysEnum) => void
  checkoutFlowType?: string
}

interface CheckoutDeliveryDaySelectState {
  deliveryOptions: DeliveryDateFragment[]
  fullAddress?: string
  selectedDeliveryDay?: CityDeliveryDaysEnum
}

const DEFAULT_STATE: CheckoutDeliveryDaySelectState = {
  deliveryOptions: [],
  fullAddress: '',
  selectedDeliveryDay: undefined,
}

export function CheckoutDeliveryDaySelect({ title, subTitle, onDeliverDaySelect, checkoutFlowType }: CheckoutDeliveryDaySelectProps): JSX.Element | null {
  const [state, setState] = useState<CheckoutDeliveryDaySelectState>({ ...DEFAULT_STATE })
  const { data: userDetailsData, loading: userDetailsLoading } = useUserDetailsQuery({ ssr: false })
  const { data: userCartData, loading: userCartLoading } = useUserCartQuery({ ssr: false })
  const [updateSubscription] = useUpdateUserSubscription()
  const [form] = useForm()
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus

  const _handleSelectDeliveryDay = async (deliveryDay: CityDeliveryDaysEnum | undefined): Promise<void> => {
    setState(prev => ({ ...prev, selectedDeliveryDay: deliveryDay }))
    if (onDeliverDaySelect) {
      onDeliverDaySelect(deliveryDay)
    }
    if (deliveryDay) {
      await updateSubscription({
        variables: {
          id: userCartData.currentUser.activeMenu.subscription.id,
          input: {
            deliveryOption: deliveryDay as unknown as WeekDayEnum,
          },
        },
        // refetchQueries: SiteHelper.getCheckoutRefetchQueries(),
        // awaitRefetchQueries: true,
      })
    }
  }

  // On mount and when delivery options change, only reset selection if options actually change
  useEffect(() => {
    if (!userCartLoading && !userDetailsLoading) {
      const defaultAddress = userDetailsData?.currentUser?.addresses?.find((address) => address.isDefault)
      const deliveryDates = userCartData?.currentUser?.activeMenu?.menu?.deliveryDates
      const deliveryOptions = [] as DeliveryDateFragment[]
      if (defaultAddress && deliveryDates) {
        const deliveryDays = defaultAddress?.location?.city?.deliveryDays
        for (const date of deliveryDates) {
          if (deliveryDays.includes(date.id as CityDeliveryDaysEnum)) {
            deliveryOptions.push(date)
          }
        }
      }
      setState(prevState => {
        // Compare previous and new delivery option ids and dates
        const prevIds = (prevState.deliveryOptions || []).map(o => `${o.id}-${o.date}`).join(',')
        const newIds = deliveryOptions.map(o => `${o.id}-${o.date}`).join(',')
        let selectedDeliveryDay = prevState.selectedDeliveryDay
        // If options changed, check if previous selection is still valid
        if (prevIds !== newIds) {
          if (!deliveryOptions.some(o => o.id === selectedDeliveryDay)) {
            selectedDeliveryDay = undefined
          }
          form.setFieldsValue({ deliveryDayRadio: selectedDeliveryDay })
          setTimeout(() => {
            if (onDeliverDaySelect) onDeliverDaySelect(selectedDeliveryDay)
          }, 0)
          return {
            ...prevState,
            deliveryOptions,
            fullAddress: defaultAddress?.location?.fullAddress || '',
            selectedDeliveryDay,
          }
        }
        // If options didn't change, just update address if needed
        if (prevState.fullAddress !== (defaultAddress?.location?.fullAddress || '')) {
          return {
            ...prevState,
            fullAddress: defaultAddress?.location?.fullAddress || '',
          }
        }
        return prevState
      })
    }
  }, [userCartData, userDetailsData, checkoutStatus])

  const getDeliveryDayOptions = (): RadioButtonProps['options'] => {
    const options = state.deliveryOptions.map(deliveryDate => {
      const formatted = format(new Date(deliveryDate.date), 'cccc do MMM yyyy')
      return {
        value: deliveryDate.id,
        element: <>
          <Heading variant='h6'>{formatted}</Heading>
          <Paragraph variant='p3'>{state.fullAddress}</Paragraph>
          <Paragraph variant='p3' bold >{`${deliveryDate.startTime ?? '09:00'} - ${deliveryDate.endTime ?? '19:00'}`}</Paragraph>
        </>,
      }
    }) ?? []
    return options
  }

  const deliveryDayRadioProps: RadioButtonProps = {
    name: 'deliveryDayRadio',
    options: getDeliveryDayOptions(),
    value: state.selectedDeliveryDay, // controlled
    variant: 'horizontal',
    error: '',
    componentTheme: 'orange',
    showLabel: false,
    showOptional: false,
    rules: [{ required: true, message: 'Please select a value' }],
    className: 'delivery-day-radio',
  }

  const onChangeHandler = (changedFields: FieldData[]): void => {
    changedFields.forEach((field) => {
      const fieldName = field.name as string | string[]
      const name = Array.isArray(fieldName) ? fieldName[0] : fieldName
      if (name === 'deliveryDayRadio') {
        const value = field.value as CityDeliveryDaysEnum | undefined
        _handleSelectDeliveryDay(value)
      }
    })
    // If user clears selection (should not be possible with radio, but for robustness):
    const currentValue = form.getFieldValue('deliveryDayRadio')
    if (!currentValue && onDeliverDaySelect) {
      onDeliverDaySelect(undefined)
    }
  }

  const loading = userCartLoading || userDetailsLoading
  const deliveryOptions = state.deliveryOptions || []

  return (
    <Choose>
      <When condition={checkoutFlowType && checkoutFlowType !== 'SUBSCRIPTION'}>
        <></>
      </When>
      <When condition={loading}>
        <SectionLoading />
      </When>
      <Otherwise>
        <If condition={deliveryOptions?.length > 0}>
          <ContentContainer>
            <Heading variant='h5'>Preferred Delivery Day</Heading>
            <If condition={!state.selectedDeliveryDay}>
              <Spacer universal='16px' />
              <Paragraph variant='p3' bold>Please select your preferred delivery day, Your delivery will repeat on this day each week. You can change it whenever you like in your account section.</Paragraph>
            </If>
            <Form form={form} onFieldsChange={onChangeHandler} className='delivery-day-form'>
              <If condition={!!title}>
                <Heading variant='h6'>{title}</Heading>
                <Spacer universal='8px' />
              </If>
              <If condition={!!subTitle}>
                <Paragraph variant='p3'>{subTitle}</Paragraph>
                <Spacer universal='8px' />
              </If>
              <RadioButton {...deliveryDayRadioProps} />
            </Form>
          </ContentContainer>
        </If>
      </Otherwise>
    </Choose>
  )
}
